import styles from './FilterBar.module.scss';
import FilterSelect from './filter-select/FilterSelect';
import { useContext, useState } from 'react';
import { AppContext } from '../../context/AppContext';
import { distanceOptions, getCategoriesToMap } from '../../lib/util';
import { ResultSortType } from '../../pages/results';
import FilterDistanceSelect from './filter-distance/FilterDistance';
import GenericButton from '../generic-button/GenericButton';
import { DownloadIcon, EmailIcon, PrintIcon } from '../icons/Icons';

interface IFilterOption {
  name: string;
  value: any;
}

const FilterBar = (props: any) => {
  const appContext = useContext(AppContext);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const specialties: IFilterOption[] = getCategoriesToMap(true, appContext).map((x) => {
    return { name: x, value: x };
  });
  const acceptanceStatuses: IFilterOption[] = [
    { name: 'Any Acceptance Status', value: '' },
    { name: 'Accepting New Patients', value: 'Yes' },
    { name: 'Not Accepting New Patients', value: 'No' }
  ];
  const filterStatuses: IFilterOption[] = [
    { name: 'None', value: ResultSortType.none },
    { name: 'Names A-Z', value: ResultSortType.namesAsc },
    { name: 'Names Z-A', value: ResultSortType.namesDesc },
    { name: 'Specialty A-Z', value: ResultSortType.specialtiesAsc },
    { name: 'Specialty Z-A', value: ResultSortType.specialtiesDesc },
    { name: 'AcceptanceStatus A-Z', value: ResultSortType.acceptanceStatusAsc },
    { name: 'AcceptanceStatus Z-A', value: ResultSortType.acceptanceStatusDesc }
  ];

  //const distances = distanceOptions();

  const downloadSearchResult = (passedProps: any) => {
    const results = passedProps.rawResults;
    
    if (!results || results.length === 0) {
      alert('No results to download');
      return;
    }

    // Extract companyId from first result
    console.log('First result:', results[0]); // Debug log to see structure
    const companyId = results[0]?.companyId || 'N/A';
    console.log('Company ID:', companyId); // Debug log

    // Show loader
    setIsGeneratingPDF(true);

    // Use setTimeout to allow the loader to render before heavy processing
    setTimeout(() => {
      try {
        import('jspdf').then(({ jsPDF }) => {
          const pdf = new jsPDF('l', 'pt', 'a4'); // 'l' for landscape orientation
          
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const margin = 40;
          const startY = 100;
          let currentY = startY;
          
          // Add title
          pdf.setFontSize(18);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Search Results', margin, 50);
          
          // Table settings - CHANGED: Specialty first (80), then Name (100)
          const columnWidths = [80, 100, 200, 80, 50, 40, 90, 80]; // Specialty, Name, Address, City, State, Zip, Phone, Accepting
          const rowHeight = 25;
          const fontSize = 9;
          const headerHeight = 22;
          
          // Draw table header
          pdf.setFontSize(fontSize);
          pdf.setFillColor(66, 139, 202);
          pdf.rect(margin, currentY - headerHeight, pageWidth - (margin * 2), headerHeight, 'F');
          
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'bold');
          const headerY = currentY - 8;
          // CHANGED: Specialty first, then Name
          pdf.text('Specialty', margin + 3, headerY);
          pdf.text('Name', margin + columnWidths[0] + 3, headerY);
          pdf.text('Address', margin + columnWidths[0] + columnWidths[1] + 3, headerY);
          pdf.text('City', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + 3, headerY);
          pdf.text('State', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + 3, headerY);
          pdf.text('Zip', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + 3, headerY);
          pdf.text('Phone', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + columnWidths[5] + 3, headerY);
          pdf.text('Accepting', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + columnWidths[5] + columnWidths[6] + 3, headerY);
          
          currentY += 5;
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
          
          let pageNumber = 1;
          let rowIndex = 0;
          
          // Draw table rows
          results.forEach((result: any, index: number) => {
            // Function to split text into lines that fit within maxWidth
            const splitTextToFit = (text: string, maxWidth: number): string[] => {
              const availableWidth = maxWidth - 6; // Padding on both sides
              if (pdf.getTextWidth(text) <= availableWidth) return [text];
              
              const words = text.split(' ');
              const lines: string[] = [];
              let currentLine = '';
              
              for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                if (pdf.getTextWidth(testLine) <= availableWidth) {
                  currentLine = testLine;
                } else {
                  if (currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                  } else {
                    // Single word is too long, split it
                    let remaining = word;
                    while (remaining) {
                      let fit = remaining;
                      while (fit && pdf.getTextWidth(fit) > availableWidth) {
                        fit = fit.slice(0, -1);
                      }
                      if (fit) {
                        lines.push(fit);
                        remaining = remaining.slice(fit.length);
                      } else {
                        break;
                      }
                    }
                  }
                }
              }
              if (currentLine) lines.push(currentLine);
              return lines;
            };
            
            // Prepare text data with word wrapping - CHANGED: Specialty first, then Name
            const specialtyLines = splitTextToFit(result.specialty || 'N/A', columnWidths[0]);
            const nameLines = splitTextToFit(result.name || 'N/A', columnWidths[1]);
            
            // Separate address components
            let addressLine = '';
            if (result.addressLine1) addressLine += result.addressLine1;
            if (result.addressLine2) addressLine += (addressLine ? ', ' : '') + result.addressLine2;
            
            const addressLines = splitTextToFit(addressLine || 'N/A', columnWidths[2]);
            const cityLines = splitTextToFit(result.city || 'N/A', columnWidths[3]);
            const stateLines = splitTextToFit(result.state || 'N/A', columnWidths[4]);
            const zipLines = splitTextToFit(result.zipCode || 'N/A', columnWidths[5]);
            const phoneLines = splitTextToFit(result.phone || 'N/A', columnWidths[6]);
            const acceptingLines = splitTextToFit(result.acceptingNewPatients || 'N/A', columnWidths[7]);
            
            // Calculate the row height needed (based on max lines in any column)
            const maxLines = Math.max(
              specialtyLines.length, 
              nameLines.length, 
              addressLines.length, 
              cityLines.length, 
              stateLines.length, 
              zipLines.length, 
              phoneLines.length, 
              acceptingLines.length
            );
            const dynamicRowHeight = maxLines * 12 + 8; // 12 points per line + padding
            
            // Check if we need a new page
            if (currentY + dynamicRowHeight > pageHeight - 60) {
              // Add page number and Plan name
              pdf.setFontSize(8);
              const footerText = `Page ${pageNumber} | Total Results: ${results.length} | Plan: ${companyId}`;
              pdf.text(footerText, margin, pageHeight - 20);
              
              pdf.addPage();
              currentY = startY;
              pageNumber++;
              
              // Redraw header on new page
              pdf.setFontSize(fontSize);
              pdf.setFillColor(66, 139, 202);
              pdf.rect(margin, currentY - headerHeight, pageWidth - (margin * 2), headerHeight, 'F');
              
              pdf.setTextColor(255, 255, 255);
              pdf.setFont('helvetica', 'bold');
              const newHeaderY = currentY - 8;
              // CHANGED: Specialty first, then Name
              pdf.text('Specialty', margin + 3, newHeaderY);
              pdf.text('Name', margin + columnWidths[0] + 3, newHeaderY);
              pdf.text('Address', margin + columnWidths[0] + columnWidths[1] + 3, newHeaderY);
              pdf.text('City', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + 3, newHeaderY);
              pdf.text('State', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + 3, newHeaderY);
              pdf.text('Zip', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + 3, newHeaderY);
              pdf.text('Phone', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + columnWidths[5] + 3, newHeaderY);
              pdf.text('Accepting', margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + columnWidths[5] + columnWidths[6] + 3, newHeaderY);
              
              currentY += 5;
              pdf.setTextColor(0, 0, 0);
              pdf.setFont('helvetica', 'normal');
              rowIndex = 0;
            }
            
            // Alternate row background
            if (rowIndex % 2 === 0) {
              pdf.setFillColor(245, 245, 245);
              pdf.rect(margin, currentY - 3, pageWidth - (margin * 2), dynamicRowHeight, 'F');
            }
            
            pdf.setFontSize(fontSize);
            
            // Draw multi-line text for each column - CHANGED: Specialty first, then Name
            const lineHeight = 12;
            const startTextY = currentY + 5;
            
            // Specialty column (now first)
            specialtyLines.forEach((line, i) => {
              pdf.text(line, margin + 3, startTextY + (i * lineHeight));
            });
            
            // Name column (now second)
            nameLines.forEach((line, i) => {
              pdf.text(line, margin + columnWidths[0] + 3, startTextY + (i * lineHeight));
            });
            
            // Address column
            addressLines.forEach((line, i) => {
              pdf.text(line, margin + columnWidths[0] + columnWidths[1] + 3, startTextY + (i * lineHeight));
            });
            
            // City column
            cityLines.forEach((line, i) => {
              pdf.text(line, margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + 3, startTextY + (i * lineHeight));
            });
            
            // State column
            stateLines.forEach((line, i) => {
              pdf.text(line, margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + 3, startTextY + (i * lineHeight));
            });
            
            // Zip column
            zipLines.forEach((line, i) => {
              pdf.text(line, margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + 3, startTextY + (i * lineHeight));
            });
            
            // Phone column
            phoneLines.forEach((line, i) => {
              pdf.text(line, margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + columnWidths[5] + 3, startTextY + (i * lineHeight));
            });
            
            // Accepting column
            acceptingLines.forEach((line, i) => {
              pdf.text(line, margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + columnWidths[5] + columnWidths[6] + 3, startTextY + (i * lineHeight));
            });
            
            currentY += dynamicRowHeight;
            rowIndex++;
          });
          
          // Add page number and Plan name to last page
          pdf.setFontSize(8);
          const footerText = `Page ${pageNumber} | Total Results: ${results.length} | Plan: ${companyId}`;
          pdf.text(footerText, margin, pageHeight - 20);
          
          // Save the PDF
          pdf.save('Search_Results.pdf');
          
          // Hide loader
          setIsGeneratingPDF(false);
        }).catch((error) => {
          console.error('Error generating PDF:', error);
          setIsGeneratingPDF(false);
          alert('Error generating PDF. Please try again.');
        });
      } catch (error) {
        console.error('Error generating PDF:', error);
        setIsGeneratingPDF(false);
        alert('Error generating PDF. Please try again.');
      }
    }, 100);
  };

  return (
    
    <div className={styles.filterBarContainer}>
      {/* Loader Overlay */}
      {isGeneratingPDF && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          flexDirection: 'column'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: 'white', marginTop: '20px', fontSize: '18px' }}>Generating PDF...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      
      <span className={styles.resultsText}>{props.resultCount || 'No'} Results Found</span>
      <FilterSelect filterData={specialties} sortBy={false} filterIndex={0} resultsFilterMethod={props.resultsFilterMethod} resultsFilterValue={props.resultsFilterValue} />
      <FilterSelect filterData={acceptanceStatuses} sortBy={false} filterIndex={1} resultsFilterMethod={props.resultsFilterMethod} resultsFilterValue={props.resultsFilterValue} />
      <FilterSelect filterData={filterStatuses} sortBy={true} filterIndex={2} resultsFilterMethod={props.resultsFilterMethod} resultsFilterValue={props.resultsFilterValue} />
      <GenericButton 
        buttonText={isGeneratingPDF ? 'Generating PDF...' : 'Download / Print Search Results'} 
        handleClick={() => !isGeneratingPDF && downloadSearchResult(props)}
      >
        <DownloadIcon/>
      </GenericButton>
      {/*
          1. Add a button here - complete
          2. Define the event onclick - complete
          3. check whether stored result is stored somewhere? - complete
          4. Or try saving in local storage - complete
          5. on Click try to fetch the local storage - complete
          6. try to print the result first
          7. formulate the result
          8. convert to PDF
      */}
      {/* { props.resultsFilterValue.name === 'Distance' ? (
        <FilterDistanceSelect />
      ) : null} */}
    </div>
  );
};

export default FilterBar;
